import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionFourTextsApi from "hooks/api/private/blocks/useSectionFourTextsApi";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionFourTextsBody, SectionFourTextsDetails } from "types/api/blocks/sectionFourTexts";
import { LanguageEnum } from "types/api/commons";

export type SectionOneTitleForm = {
    id: string | undefined;
    text: {
        [x in LanguageEnum]: {
            text_one: string;
            text_two: string;
            text_three: string;
            text_four: string;
        };
    };
};

type SectionOneTitleTitleType = {
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneTitleForm>;
};

function useSectionOneTitle(): SectionOneTitleTitleType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const { saveSectionFourTexts, getOneSectionFourTexts, deleteSectionFourTexts } =
        useSectionFourTextsApi();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text_one: {
                                type: "simple",
                                validators: [],
                            },
                            text_two: {
                                type: "simple",
                                validators: [],
                            },
                            text_three: {
                                type: "simple",
                                validators: [],
                            },
                            text_four: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: SectionOneTitleForm = React.useMemo(() => {
        return {
            id: undefined,
            text: {
                it: {
                    text_one: "",
                    text_two: "",
                    text_three: "",
                    text_four: "",
                },
                en: {
                    text_one: "",
                    text_two: "",
                    text_three: "",
                    text_four: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionOneTitleForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionOneTitleDetails, setSectionOneTitleDetails] = React.useState<
        SectionFourTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionFourTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionOneTitleDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionOneTitleDetails, getOneSectionFourTexts, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionOneTitleDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionOneTitleDetails.id;
                newForm.text = sectionOneTitleDetails.text;

                return newForm;
            });
        }
    }, [setForm, sectionOneTitleDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionOneTitleBody: SectionFourTextsBody = {
                    id: blockGroupCompositionId,
                    text: {
                        it: {
                            text_one: form.text.it.text_one,
                            text_two: form.text.it.text_two,
                            text_three: form.text.it.text_three,
                            text_four: form.text.it.text_four,
                        },
                        en: {
                            text_one: form.text.en.text_one,
                            text_two: form.text.en.text_two,
                            text_three: form.text.en.text_three,
                            text_four: form.text.en.text_four,
                        },
                    },
                };

                const [apiCall] = saveSectionFourTexts(sectionOneTitleBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionFourTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionOneTitleTitleType => ({
            buttonDelete: deleteSectionFourTexts,
            formHook,
        }),
        [deleteSectionFourTexts, formHook]
    );
}

export default useSectionOneTitle;
